import * as React from 'react';

/**
 * Components.
 */
import Grid from '../../layoutComponents/Grid';
import Box from '../../layoutComponents/Box';
import { Visibility, VisibilityOff } from '@mui/icons-material';

/**
 * Styled components.
 */
import {
  StyledInput,
  InputIconWrapper,
  PasswordButton,
  StyledInputProps
} from './Input.style';

/**
 * Interface for Input props for the Input component to implement
 *
 * @interface IInputProps
 */
export interface IInputProps
  extends StyledInputProps,
    Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      keyof StyledInputProps | 'color'
    > {
  /**
   * Type of input.
   */
  type: 'text' | 'email' | 'password' | 'tel' | 'search' | 'number' | 'url';

  /**
   * Unique identifier for this input. Required for screen readers
   * to associate this component with the label that describes it.
   */
  id: string;

  /**
   * Input placeholder
   */
  placeholder?: string;
  /**
   * Value for controlled inputs
   */
  value?: string;
  /**
   * Max length of the input
   */
  maxLength?: number;
  /**
   * On change handler passed to input
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /**
   * Sets input to be disabled
   */
  disabled?: boolean;
  /**
   * Sets input to be in an error state
   */
  hasError?: boolean;
  /**
   * Auto focuses input if true
   */
  autoFocus?: boolean;
  /**
   * Default value for the input
   */
  defaultText?: string;

  /**
   * Icon to render to the right of the input.
   */
  icon?: React.ReactElement | string;
}

/**
 * Returns an Input to be used within the Form component. The required prop `type`
 * renders the input as password, url, search, tel, number, email or text. If the
 * input is rendered as a password, it will also render a show password toggle
 * to toggle the kind from password to text.
 *
 *
 * @return Component to render input based on type.
 */
const Input = React.forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      children,
      disabled,
      hasError,
      autoFocus,
      value,
      maxLength,
      defaultText,
      onChange,
      type,
      placeholder,
      icon,
      ...props
    },
    ref
  ) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const [__value, setValue] = React.useState(defaultText || '');
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const [__passwordToggle, setPasswordToggle] = React.useState(false);

    /**
     * On change handler for input that uses change handler from props or
     * sets state internally if no change handler is passed in.
     *
     * @param event The native event passed to change handler
     */
    const changeHandler =
      typeof onChange !== 'undefined'
        ? onChange
        : (event: React.ChangeEvent<HTMLInputElement>) => {
            setValue(event.currentTarget.value);
          };

    // Toggles type based on show password
    const interpolatedType = __passwordToggle ? 'text' : type;

    // eslint-disable-next-line no-nested-ternary
    const paddingRight = type === 'password' ? 6 : icon ? 7 : 2;

    // Base styled input with props to compose rendered input out of
    const Base = (
      <StyledInput
        py={2}
        px={2}
        pr={paddingRight}
        ref={ref}
        width={1}
        fontSize={0}
        disabled={disabled}
        hasError={hasError}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={changeHandler}
        maxLength={maxLength}
        type={interpolatedType}
        value={value || __value}
        {...(props as any)}
      />
    );

    /**
     * If the input is of `type` password, then render it with a switch to
     * toggle hidden text. Otherwise, render the correct element.
     */
    const BaseInput =
      type === 'password' ? (
        <Grid flexDirection="column" position="relative" width={1}>
          {Base}
          <Box position="absolute" right={0} top={0}>
            <PasswordButton
              m={0}
              p={2}
              kind="text"
              role="switch"
              aria-label="Toggle password visibility"
              aria-checked={__passwordToggle}
              disabled={disabled}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.preventDefault();
                setPasswordToggle(!__passwordToggle);
              }}
            >
              {__passwordToggle ? <Visibility /> : <VisibilityOff />}
            </PasswordButton>
          </Box>
        </Grid>
      ) : (
        <Grid flexDirection="column" position="relative" width={1}>
          {Base}
          {icon && (
            <InputIconWrapper disabled={disabled}>{icon}</InputIconWrapper>
          )}
        </Grid>
      );
    return <React.Fragment>{BaseInput}</React.Fragment>;
  }
);

Input.displayName = 'Input';
export default Input;
